import { Integration, IntegrationStatus } from '@/shared/types/integration';

/**
 * Display information for integration status
 *
 * @property label - Translated status label for display
 * @property variant - Badge variant determining the visual style and color
 * @property tooltip - Optional translated hover text explaining the status and next step
 */
export interface IntegrationStatusDisplay {
  label: string;
  variant: 'successful' | 'neutral' | 'warning' | 'destructive';
  tooltip?: string;
}

// Per-status tooltip key. Until integrations-controller persists a structured
// error_code alongside `status` (tracked separately), we can only message at the
// granularity of the IntegrationStatus enum — multiple distinct failure modes
// (revoked webhooks, expired token, rotated credentials) collapse onto a single
// status here. Keep copy generic enough to stay accurate across that range.
const ERROR_TOOLTIP_KEYS: Partial<Record<IntegrationStatus, string>> = {
  [IntegrationStatus.FORBIDDEN]: 'status.tooltip.forbidden',
  [IntegrationStatus.INVALID_AUTHENTICATION]: 'status.tooltip.invalid_authentication',
  [IntegrationStatus.ACCOUNT_BLOCKED]: 'status.tooltip.account_blocked',
  [IntegrationStatus.BAD_REQUEST]: 'status.tooltip.bad_request',
  [IntegrationStatus.NOT_FOUND]: 'status.tooltip.not_found',
  [IntegrationStatus.API_VERSION_NOT_SUPPORTED]: 'status.tooltip.api_version_not_supported',
  [IntegrationStatus.RATE_LIMIT]: 'status.tooltip.rate_limit',
  [IntegrationStatus.GENERAL_ERROR]: 'status.tooltip.general_error',
  [IntegrationStatus.INTERNAL_SERVER]: 'status.tooltip.internal_server',
};

/**
 * Determines the display properties for an integration status
 *
 * @param integration - The integration object containing status and active state
 * @param t - Translation function from react-i18next
 * @returns Display properties with label, badge variant, and optional tooltip
 */
export function getIntegrationStatusDisplay(
  integration: Pick<Integration, 'status' | 'active'>,
  t: (key: string) => string
): IntegrationStatusDisplay {
  const { status, active } = integration;

  if (status === IntegrationStatus.VERIFIED) {
    return active
      ? { label: t('status.active'), variant: 'successful' }
      : { label: t('status.disabled'), variant: 'neutral' };
  }

  if (status === IntegrationStatus.UNVERIFIED) {
    return {
      label: t('status.setup_required'),
      variant: 'neutral',
      tooltip: t('status.tooltip.setup_required'),
    };
  }

  if (status === IntegrationStatus.MISSING_CREDENTIALS) {
    return {
      label: t('status.missing_credentials'),
      variant: 'warning',
      tooltip: t('status.tooltip.missing_credentials'),
    };
  }

  if (status === IntegrationStatus.MISSING_PERMISSIONS) {
    return {
      label: t('status.missing_permissions'),
      variant: 'warning',
      tooltip: t('status.tooltip.missing_permissions'),
    };
  }

  if (status === IntegrationStatus.VERIFYING) {
    return {
      label: t('status.verifying'),
      variant: 'neutral',
      tooltip: t('status.tooltip.verifying'),
    };
  }

  // All other statuses are errors (invalid-authentication, invalid_config, API errors, etc.)
  const tooltipKey = status ? ERROR_TOOLTIP_KEYS[status] : undefined;
  return {
    label: t('status.error'),
    variant: 'destructive',
    tooltip: t(tooltipKey ?? 'status.tooltip.error'),
  };
}
